import {type Dispatch, type SetStateAction} from 'react';
import {create} from 'zustand';

import {EMPTY_HISTORY_KEY_INFO, type RunHistoryKeyInfo} from '../../types/run';
import {useIsNgwEnabled} from '../../util/rampFeatureFlags/rampFeatureFlags';

export type MaybeTruncatedCount = {
  count: number;
  isTruncated: boolean;
};

export type PanelGenHistoryKeysStoreValue = {
  /** Truncated/sampled set of keys to use for workspace auto panel gen */
  sampledHistoryKeys: RunHistoryKeyInfo;
  defaultSectionsInfo: {
    /**
     * Count of ALL auto/default sections matching the current run + panel filters,
     * including ones that fall outside the truncation limits.
     */
    numTotalMatches: number;
    /** Count of sections that match search/filters, but were excluded due to max prefix groups limit */
    numExcludedMatches: number;
  };
  /** Counts of keys matching the current run and panel filters, by default section name */
  matchedKeysBySection: Record<string, MaybeTruncatedCount>;
  /**
   * The highest step logged by the runs matching the current run filter, or -1
   * when none of them has logged history. Undefined until the first response.
   *
   * The server computes this over the whole matched run set, so it ignores the
   * panel search and the group/metric caps: a search that matches nothing still
   * reports the run set's real last step.
   *
   * It is deliberately not folded into `sampledHistoryKeys`. When a poll
   * returns the same keys, the sync keeps the previous `sampledHistoryKeys`
   * object so the panel bank does not regenerate (see
   * `sampledHistoryKeysEqual`, which compares key names and types and ignores
   * the step). A step stored inside that object would be retained along with
   * it and stop advancing: an empty project would stay at -1 even once its
   * runs started logging.
   */
  lastStep?: number;
  /**
   * The panel search string that the currently stored `sampledHistoryKeys` reflect.
   * Lags behind the user's live search value while fetching updated results.
   * Used to determine if panel search loading state should be shown.
   * We can't just use the query loading state because it doesn't account for
   * the debounce period before the query is actually sent.
   */
  resultsSearchQuery: string;
  isLoading: boolean;
  /**
   * The query error, if any, so consumers can rethrow it into the workspace
   * error boundary (which sends the actual error to Datadog).
   */
  error?: Error;
  /**
   * User-facing message when panel search was rejected for an unsupported
   * regex. Undefined otherwise.
   */
  searchErrorMessage?: string;
};

type PanelGenHistoryKeysStore = PanelGenHistoryKeysStoreValue & {
  setResult: Dispatch<SetStateAction<PanelGenHistoryKeysStoreValue>>;
  /** Reset the (module-global) store back to its initial state. */
  reset: () => void;
};

const INITIAL_VALUE: PanelGenHistoryKeysStoreValue = {
  sampledHistoryKeys: EMPTY_HISTORY_KEY_INFO,
  defaultSectionsInfo: {numTotalMatches: 0, numExcludedMatches: 0},
  matchedKeysBySection: {},
  lastStep: undefined,
  isLoading: true,
  error: undefined,
  resultsSearchQuery: '',
  searchErrorMessage: undefined,
};

const usePanelGenHistoryKeysStore = create<PanelGenHistoryKeysStore>(set => ({
  ...INITIAL_VALUE,
  setResult: valueOrUpdater =>
    set(state => {
      const {setResult: _, ...currentValue} = state;
      return typeof valueOrUpdater === 'function'
        ? valueOrUpdater(currentValue)
        : valueOrUpdater;
    }),
  reset: () => set(() => ({...INITIAL_VALUE})),
}));

export const useSampledHistoryKeys = (): RunHistoryKeyInfo =>
  usePanelGenHistoryKeysStore(s => s.sampledHistoryKeys);

export const usePanelGenHistoryKeysIsLoading = (): boolean =>
  usePanelGenHistoryKeysStore(s => s.isLoading);

/** The panel-gen history keys query error, if any (for rethrowing). */
export const usePanelGenHistoryKeysError = (): Error | undefined =>
  usePanelGenHistoryKeysStore(s => s.error);

export const useResetPanelGenHistoryKeys = (): (() => void) =>
  usePanelGenHistoryKeysStore(s => s.reset);

/**
 * The last step logged by the current run set, or undefined before the first
 * response. Only meaningful under NGW; read it through
 * `useActiveHistoryKeyInfo` rather than directly.
 */
export const usePanelGenLastStep = (): number | undefined =>
  usePanelGenHistoryKeysStore(s => s.lastStep);

export const useDefaultSectionsInfo = () =>
  usePanelGenHistoryKeysStore(s => s.defaultSectionsInfo);

export const usePanelGenHistoryKeysResultsSearchQuery = () =>
  usePanelGenHistoryKeysStore(s => s.resultsSearchQuery);

export const usePanelGenSearchErrorMessage = (): string | undefined =>
  usePanelGenHistoryKeysStore(s => s.searchErrorMessage);

/**
 * Returns the count of keys matching the current run and panel filters for an
 * auto-generated section, as well as whether the section is truncated (i.e.
 * has more matching keys than query returned)
 */
export const useDefaultSectionMatchedKeys = (defaultSectionName?: string) => {
  const isNgwEnabled = useIsNgwEnabled();
  const matchedKeysBySection = usePanelGenHistoryKeysStore(
    s => s.matchedKeysBySection
  );
  if (!isNgwEnabled || !defaultSectionName) {
    return null;
  }
  return matchedKeysBySection[defaultSectionName];
};

export const useSetPanelGenHistoryKeysResult =
  (): PanelGenHistoryKeysStore['setResult'] =>
    usePanelGenHistoryKeysStore(s => s.setResult);
